import React, { useEffect, useRef, useState } from "react";
import {
  Table,
  Spin,
  Form,
  Select,
  Button,
  DatePicker,
  Space,
  Popconfirm,
  Tag,
  Input,
  InputRef,
} from "antd";
import type { DatePickerProps, RangePickerProps } from "antd/es/date-picker";
import { useNavigate } from "react-router-dom";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { useApplicationContext } from "../../application/ApplicationContext";
import type { ColumnsType, ColumnType } from "antd/es/table";
import {
  getAllAccommodations,
  deleteAccommodation,
} from "../../services/AccommodationService";
import "./AccommodationListPage.scss";
import {
  AccommodationDto,
  CreateAccommodationDto,
} from "../../models/Accommodation";
import type { FilterConfirmProps } from "antd/es/table/interface";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { Toast } from "primereact/toast";
import CustomButton from "../../components/AccommodationData/CustomButton/CustomButton";
import { formatDate, formatTime } from "../../core/Utility";
///// REDUX IMPORT
import { useDispatch, useSelector } from "react-redux";
import { setCurrentPage } from "../../store-redux/slices/ChangePageSlice";

const AccommodationListPage = () => {
  const [activeSpinner, setActiveSpinner] = useState<boolean>(false);
  const [accommodationList, setAccommodationList] = useState<
    CreateAccommodationDto[]
  >([]);
  const [yearOptions, setYearOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [accommodationOptions, setAccommodationOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [deleteId, setDeleteId] = useState<string>("");
  const [sort, setSort] = useState({});
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const { RangePicker } = DatePicker;
  const searchInput = useRef<InputRef>(null);
  const [searchText, setSearchText] = useState<string>("");
  const [searchedColumn, setSearchedColumn] = useState<string>("");
  const toast = useRef<any>(null);
  const [pageNumber, setPageNumber] = useState<any>(1);
  const [paginationNumber, setPaginationNumber] = useState<any>(30);
  const navigate = useNavigate();
  const { setBreadcrumb } = useApplicationContext();

  ///// IMPLEMENTATION REDUX
  const dispatch = useDispatch();
  const currentPage = useSelector((state: any) => state.changePage.currentPage);
  ///// END IMPLEMENTATION REDUX

  // console.log("ddd")

  useEffect(() => {
    setBreadcrumb(["Smeštaj", "Lista smeštaja"]);
    fetchAllAccommodation();
  }, []);

  const fetchAllAccommodation = () => {
    setActiveSpinner(true);
    getAllAccommodations()
      .then((response: any) => {
        setAccommodationList(response.data.result);
        return response.data.result;
      })
      .then((accommodations) => {
        //set accommodation filter options
        let newAccommodationOptions: { value: string; label: string }[] = [];
        accommodations?.map((item: any) => {
          newAccommodationOptions.push({ value: item.name, label: item.name });
        });
        newAccommodationOptions = newAccommodationOptions.filter(
          (
            value,
            index,
            self //remove duplicate
          ) => index === self.findIndex((t) => t.label === value.label)
        );
        setAccommodationOptions(newAccommodationOptions);
        //set year filter options
        //TODO: set real values from events
        setYearOptions([
          { value: "2023.", label: "2023." },
          { value: "2022.", label: "2022." },
          { value: "2021.", label: "2021." },
        ]);

        setPageNumber(currentPage); /// - SET NUMBER PAGE FROM STORE
      })
      .finally(() => setActiveSpinner(false))
      .catch((err: any) => console.log(err));
  };

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: DataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  type DataIndex = keyof AccommodationDto;
  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): ColumnType<AccommodationDto> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Pretraži po imenu`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              handleSearch(selectedKeys as string[], confirm, dataIndex);
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90, border: "none" }}
          >
            Pretraži
          </Button>
          <Button
            onClick={() => {
              clearFilters && handleReset(clearFilters);
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              close();
              setSearchedColumn("");
            }}
            size="small"
            style={{ width: 90 }}
          >
            Poništi
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#C7363D" : undefined }} />
    ),
    onFilter: (
      value,
      record //@ts-ignore
    ) =>
      (record[dataIndex] ?? "")
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
            color: "#0D4077",
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns: ColumnsType<CreateAccommodationDto | any> = [
    {
      title: "Red. broj",
      width: 100,
      dataIndex: "firstName",
      key: "firstName",
      render: (firstName, text, index) => {
        return (
          <span className="blue-text">
            {(pageNumber - 1) * paginationNumber + index + 1}
          </span>
        );
      },
    },
    {
      title: "Naziv smeštaja",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => (a.name < b.name ? -1 : 1),
      render: (name) => <span className="blue-text">{name}</span>,
      ...getColumnSearchProps("name"),
    },
    {
      title: "Adresa smeštaja",
      dataIndex: "address",
      key: "address",
      sorter: (a, b) => (a.address < b.address ? -1 : 1),
      render: (address) => <span className="blue-text">{address}</span>,
      ...getColumnSearchProps("address"),
    },
    {
      title: "Datum kreiranja",
      dataIndex: "createdAt",
      key: "createdAt",
      defaultSortOrder: "descend",
      sorter: (a, b) =>
        new Date(a?.createdAt).getTime() < new Date(b?.createdAt).getTime()
          ? -1
          : 1,
      render: (createdAt) => {
        return (
          <span className="blue-text">
            {`${formatDate(new Date(createdAt))} ${formatTime(
              new Date(createdAt)
            )}h`}
          </span>
        );
      },
    },
    {
      title: "Kapacitet sala",
      dataIndex: "hallCapacity",
      key: "hallCapacity",
      align: "center",
      sorter: (a, b) => (a.hallCapacity < b.hallCapacity ? -1 : 1),
      render: (hallCapacity) => (
        <span className="blue-text">{hallCapacity}</span>
      ),
    },
    {
      title: "Kapacitet smeštaja",
      dataIndex: "totalNumberOfRooms",
      key: "totalNumberOfRooms",
      align: "center",
      sorter: (a, b) => (a.totalNumberOfRooms < b.totalNumberOfRooms ? -1 : 1),
      render: (totalNumberOfRooms) => (
        <span className="blue-text">{totalNumberOfRooms}</span>
      ),
    },
    {
      title: "Kontakt osoba",
      dataIndex: "contactName",
      key: "contactName",
      align: "center",
      sorter: (a, b) => (a.contactName < b.contactName ? -1 : 1),
      render: (contactName) => <span className="blue-text">{contactName}</span>,
      ...getColumnSearchProps("contactName"),
    },
    {
      title: "Kontakt telefon",
      dataIndex: "contactPhone",
      key: "contactPhone",
      align: "center",
      sorter: (a, b) => (a.contactPhone < b.contactPhone ? -1 : 1),
      render: (contactPhone) => (
        <span className="blue-text">{contactPhone}</span>
      ),
      ...getColumnSearchProps("contactPhone"),
    },
    {
      title: "Tagovi",
      dataIndex: "tags",
      key: "tags",
      align: "center",
      render: (_, { tags }: any) => (
        <>
          {tags !== null &&
            tags?.map((tag: any, index: number) => {
              const colors = ["orange", "geekblue", "green", "blue", "red"];
              let color = colors[index];
              return (
                <Tag color={color} key={tag}>
                  {tag}
                </Tag>
              );
            })}
        </>
      ),
    },
    {
      title: "Opcije",
      fixed: "right",
      width: 250,
      key: "action",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <span
            className="action-button"
            onClick={() => handleEditButton(record.id as string)}
          >
            Izmena/Pregled
          </span>
          {/* <span
            className="action-button"
            onClick={() => handleViewButton(record.id as string)}
          >
            View
          </span> */}
          <Popconfirm
            title="Brisanje smeštaja?"
            description="Da li ste sigurni da želite da izbrišete smeštaj?"
            onConfirm={() => {
              setActiveSpinner(true);
              deleteAccommodation(deleteId)
                .then(() => {
                  toast?.current?.show({
                    severity: "success",
                    detail: "Uspešno izbrisan smeštaj",
                    life: 3000,
                  });
                  fetchAllAccommodation();
                })
                .finally(() => setActiveSpinner(false))
                .catch((err) => {
                  toast?.current?.show({
                    severity: "error",
                    detail: err.response.data.message,
                    life: 3000,
                  });
                });
            }}
            onCancel={() => {}}
            okText="Da"
            cancelText="Ne"
          >
            <span
              className="action-button"
              onClick={() => setDeleteId(record.id as string)}
            >
              Brisanje
            </span>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handeSelection = (name: string, newValue: any) => {
    setSort({ ...sort, [name]: newValue });
  };

  const handleRangeDatePicker = (
    value: DatePickerProps["value"] | RangePickerProps["value"],
    dateString: [string, string] | string
  ) => {
    setSort({ ...sort, startDate: dateString[0], endDate: dateString[1] });
  };

  const handleEditButton = (id: string) => {
    navigate(`/edit-accommodation/${id}`);
  };

  const handleViewButton = (id: string) => {
    navigate(`/accommodation-overview/${id}`);
  };

  const handleOpenFilters = () => {
    setOpenFilter(!openFilter);
  };

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="accommodation-list">
        <Spin spinning={activeSpinner}>
          <div className="btn-container">
            {/* <CustomButton
              danger={false}
              htmlType={"submit"}
              type={"primary"}
              function={() => {
                handleOpenFilters();
              }}
              text={"Dodatni filteri"}
            /> */}
          </div>
          {openFilter ? (
            <div className="form-container">
              <Form.Item name="role" label="Godina:">
                <Select
                  placeholder="Izaberite godinu"
                  options={yearOptions}
                  onChange={(newValue) => handeSelection("year", newValue)}
                />
              </Form.Item>
              <Form.Item name="role" label="Naziv smeštaja:">
                <Select
                  placeholder="Izaberite naziv smeštaja"
                  options={accommodationOptions}
                  onChange={(newValue) =>
                    handeSelection("accommodation", newValue)
                  }
                />
              </Form.Item>
              <div className="date-container">
                <Form.Item name="role" label="Odabrani datum:" />
                <RangePicker
                  className="range-date"
                  showTime={false}
                  format="YYYY-MM-DD"
                  onChange={handleRangeDatePicker}
                />
              </div>
            </div>
          ) : null}
          <div className="table">
            <Table
              scroll={{ x: 1800 }}
              sticky={true}
              columns={columns}
              pagination={{
                current: pageNumber,
                onChange(current) {
                  setPageNumber(current);
                  dispatch(setCurrentPage(current)); /// DISPATCH ACTION FROM REDUX
                },
                defaultPageSize: 30,
                showSizeChanger: true,
                pageSizeOptions: ["30", "50", "100"],
              }}
              rowKey={(data) => {
                return data.id;
              }}
              dataSource={accommodationList}
              onChange={(pagination, filters, sorter, extra) => {
                setPaginationNumber(pagination.pageSize);
              }}
              className="table-container"
            />
          </div>
        </Spin>
      </div>
    </PageLayout>
  );
};

export default AccommodationListPage;
